import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGroups,
  getGroupById,
  deleteGroup,
  GroupFilters,
  createGroup,
  updateGroup,
  cloneGroup,
  GroupCreatePayload,
  GroupUpdatePayload,
  CloneRolePayload,
} from '../../services';

// Query keys factory
export const groupKeys = {
  all: ['groups'] as const,
  lists: () => [...groupKeys.all, 'list'] as const,
  list: (filters: GroupFilters) =>
    [...groupKeys.lists(), { ...filters }] as const,
  details: () => [...groupKeys.all, 'detail'] as const,
  detail: (id: number) => [...groupKeys.details(), id] as const,
};

// List all groups/roles
export function useGroups(filters: GroupFilters) {
  return useQuery({
    queryKey: groupKeys.list(filters),
    queryFn: () => getGroups(filters),
    placeholderData: (previousData) => previousData,
  });
}

// Single group query
export function useGroup(id: number) {
  return useQuery({
    queryKey: groupKeys.detail(id),
    queryFn: () => getGroupById(id),
    enabled: !!id,
  });
}

// Create mutation
export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GroupCreatePayload) => createGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

// Update mutation
export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: GroupUpdatePayload }) =>
      updateGroup(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(id) });
    },
  });
}

// Delete group
export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteGroup(id),
    onSuccess: () => {
      // refetch list after delete
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to delete group', error);
    },
  });
}

// Clone role mutation
export function useCloneGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CloneRolePayload }) =>
      cloneGroup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to clone role', error);
    },
  });
}
